import React, { useEffect, useMemo, useState } from 'react';
import InventoryBarcodeScanner from './InventoryBarcodeScanner';
import { getBarcodeMap, normalizeInventoryBarcode } from './inventoryScannerModel';
import { parseInventoryBarcodePayload } from './gs1BarcodeModel';
import {
  barcodeMappedCatalogIds,
  getGlobalBarcodeMap,
  resolveBarcodeCatalogId,
} from './inventoryBarcodeLibraryModel';
import { PHYSICAL_INVENTORY_TARGETS } from './inventoryUnitModel';
import {
  buildSeasonCloseoutPlan,
  findSeasonCloseoutMedicationRows,
  findSeasonCloseoutScanCandidates,
  makeSeasonCloseoutReturnRecord,
  makeSeasonCloseoutReturnRecords,
  mergeSeasonCloseoutManifest,
  summarizeSeasonCloseoutManifest,
} from './seasonCloseoutModel';
import './season-closeout.css';

function todayText() {
  const date = new Date();
  const offset = date.getTimezoneOffset();
  return new Date(date.getTime() - offset * 60000).toISOString().slice(0, 10);
}

function formatDate(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ''));
  if (!match) return value || 'N/A';
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12, 0, 0, 0);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleDateString();
}

function candidateFingerprint(row) {
  return [row?.catalogId, row?.lotSerial, row?.expirationDate].map(value => String(value || '').trim().toLowerCase()).join('|');
}

function closeoutTargets() {
  return PHYSICAL_INVENTORY_TARGETS.filter(target => !['clipboard'].includes(target.type));
}

function recordId(row) {
  return String(row?.record?.recordId || '').trim();
}

export default function SeasonCloseout() {
  const [data, setData] = useState({ catalog: [], expirations: [], settings: {} });
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [targetId, setTargetId] = useState('LM-PACK-01');
  const [closeoutYear, setCloseoutYear] = useState(new Date().getFullYear());
  const [started, setStarted] = useState(false);
  const [savingId, setSavingId] = useState('');
  const [pendingCandidates, setPendingCandidates] = useState([]);
  const [pendingScanLabel, setPendingScanLabel] = useState('');
  const [pendingMedicationRows, setPendingMedicationRows] = useState([]);
  const [pendingMedicationLabel, setPendingMedicationLabel] = useState('');
  const [returnManifest, setReturnManifest] = useState([]);
  const [barcodeMap, setBarcodeMap] = useState({});

  const load = async () => {
    setStatus('loading');
    setError('');
    try {
      const response = await fetch('/api/tracking?admin=inventory', { cache: 'no-store' });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload?.error || 'Unable to load seasonal inventory.');
      setData(payload);
      if (typeof window !== 'undefined') setBarcodeMap(getBarcodeMap(window.localStorage));
      setStatus('ready');
    } catch (err) {
      setError(err?.message || 'Unable to load seasonal inventory.');
      setStatus('error');
    }
  };

  useEffect(() => { load(); }, []);

  const targets = useMemo(() => closeoutTargets(), []);
  const target = targets.find(item => item.id === targetId) || targets[0];
  const plan = useMemo(
    () => buildSeasonCloseoutPlan(data.catalog, data.expirations, targetId, closeoutYear),
    [data.catalog, data.expirations, targetId, closeoutYear],
  );
  const globalBarcodeMap = useMemo(() => getGlobalBarcodeMap(data.expirations), [data.expirations]);
  const outstandingCatalogIds = useMemo(() => new Set(plan.rows.map(row => row.catalogId)), [plan.rows]);
  const manifestSummary = useMemo(() => summarizeSeasonCloseoutManifest(returnManifest), [returnManifest]);
  const pendingMedicationSummary = useMemo(() => summarizeSeasonCloseoutManifest(pendingMedicationRows), [pendingMedicationRows]);
  const manifestRecordIds = useMemo(() => new Set(returnManifest.map(recordId).filter(Boolean)), [returnManifest]);

  const postRecords = async records => {
    const response = await fetch('/api/tracking?admin=inventory', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ records }),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(payload?.error || 'Unable to update season closeout inventory.');
    setData(payload);
    return payload;
  };

  const postRecord = record => postRecords([record]);

  const clearPending = () => {
    setPendingCandidates([]);
    setPendingScanLabel('');
    setPendingMedicationRows([]);
    setPendingMedicationLabel('');
  };

  const returnRow = async row => {
    if (!row?.record?.recordId) return;
    setSavingId(row.record.recordId);
    setError('');
    setMessage('');
    clearPending();
    try {
      await postRecord(makeSeasonCloseoutReturnRecord(row.record, closeoutYear, todayText()));
      setMessage(`${row.item}${row.lotSerial ? ` · Lot ${row.lotSerial}` : ''} scanned out of ${target.name} and returned to Logistics.`);
    } catch (err) {
      setError(err?.message || 'Unable to scan this item out.');
    } finally {
      setSavingId('');
    }
  };

  const stageMedicationRows = () => {
    if (!pendingMedicationRows.length) return;
    const next = mergeSeasonCloseoutManifest(returnManifest, pendingMedicationRows);
    const added = next.length - returnManifest.length;
    setReturnManifest(next);
    setMessage(`${pendingMedicationRows[0]?.item || 'Medication'} confirmed. ${added} saved lot/date record${added === 1 ? '' : 's'} added to the return manifest.`);
    clearPending();
  };

  const removeManifestRow = id => {
    setReturnManifest(current => current.filter(row => recordId(row) !== id));
    setMessage('Removed from the staged return manifest. Active inventory was not changed.');
  };

  const confirmReturnManifest = async () => {
    if (!returnManifest.length) return;
    const summary = summarizeSeasonCloseoutManifest(returnManifest);
    const confirmed = window.confirm(
      `Confirm ${summary.units} medication unit${summary.units === 1 ? '' : 's'} (${summary.medicationCount} medication${summary.medicationCount === 1 ? '' : 's'}) physically returned from ${target.name}?\n\nThis final step removes them from active inventory and records the season-closeout return for Archive history.`
    );
    if (!confirmed) return;

    setSavingId('manifest');
    setError('');
    setMessage('');
    clearPending();
    try {
      const records = makeSeasonCloseoutReturnRecords(returnManifest, closeoutYear, todayText());
      await postRecords(records);
      setReturnManifest([]);
      setMessage(`${summary.units} medication unit${summary.units === 1 ? '' : 's'} confirmed returned to Logistics and recorded for season-closeout Archive history.`);
    } catch (err) {
      setError(err?.message || 'Unable to confirm the medication return manifest. Nothing was removed from the staged list.');
    } finally {
      setSavingId('');
    }
  };

  const handleScan = raw => {
    setError('');
    setMessage('');
    clearPending();
    if (!started) return setError('Click Begin season closeout before scanning items out.');
    if (!plan.rows.length) return setMessage(`${target.name} has no remaining closeout items.`);

    const scan = parseInventoryBarcodePayload(raw);
    if (!scan?.primaryLookupKey) return setError('The scanner did not return a usable barcode.');

    const mappedIds = barcodeMappedCatalogIds(scan.lookupKeys, globalBarcodeMap);
    const legacyRaw = normalizeInventoryBarcode(scan.raw);
    const legacyCatalogId = barcodeMap[legacyRaw] || '';
    const resolvedLocalOrGlobal = resolveBarcodeCatalogId(scan.lookupKeys, globalBarcodeMap, barcodeMap, outstandingCatalogIds);
    const candidateIds = [...new Set([
      ...mappedIds.filter(id => outstandingCatalogIds.has(id)),
      ...(resolvedLocalOrGlobal ? [resolvedLocalOrGlobal] : []),
      ...(legacyCatalogId && outstandingCatalogIds.has(legacyCatalogId) ? [legacyCatalogId] : []),
    ])];

    if (!candidateIds.length) {
      setError(`Barcode ${scan.primaryLookupKey} is not mapped to anything currently required from ${target.name}. Verify the item or use the manual pull button.`);
      return;
    }

    const medicationRows = findSeasonCloseoutMedicationRows(plan.rows, candidateIds);
    if (medicationRows.length) {
      const medicationCatalogIds = [...new Set(medicationRows.map(row => row.catalogId))];
      if (medicationCatalogIds.length > 1) {
        setError('This barcode maps to more than one medication on the closeout list. Use the required pull list below to choose the correct medication before confirming lots/dates.');
        return;
      }

      const unstaged = medicationRows.filter(row => !manifestRecordIds.has(recordId(row)));
      if (!unstaged.length) {
        setMessage(`${medicationRows[0]?.item || 'This medication'} is already completely staged in the return manifest.`);
        return;
      }

      setPendingMedicationRows(unstaged);
      setPendingMedicationLabel(scan.primaryLookupKey);
      setMessage(`Medication recognized. Confirm the saved lot/expiration records you physically have; you do not need to retype them.`);
      return;
    }

    const candidates = findSeasonCloseoutScanCandidates(plan.rows, candidateIds, scan);
    if (!candidates.length) {
      setError('The product is on the closeout list, but its scanned lot / expiration does not match an active saved unit. Check the physical package against the list below.');
      return;
    }

    const fingerprints = new Set(candidates.map(candidateFingerprint));
    if (candidates.length === 1 || fingerprints.size === 1) {
      returnRow(candidates[0]);
      return;
    }

    setPendingCandidates(candidates);
    setPendingScanLabel(scan.primaryLookupKey);
    setMessage('This barcode matches more than one saved lot/date. Choose the physical unit you are holding.');
  };

  const chooseTarget = id => {
    setTargetId(id);
    setStarted(false);
    clearPending();
    setReturnManifest([]);
    setError('');
    setMessage('');
  };

  const grouped = useMemo(() => {
    const map = new Map();
    plan.rows.forEach(row => {
      const key = row.component || 'Other';
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(row);
    });
    return [...map.entries()];
  }, [plan.rows]);

  return (
    <div className="inventory-root">
      <header className="inventory-topbar">
        <div>
          <h1>Line Medic Season Closeout</h1>
          <p>Pull every medication and next-calendar-year expiration from each physical container</p>
        </div>
        <span>Private admin</span>
      </header>

      <main className="inventory-page season-closeout-page">
        {error && <div className="inventory-error">{error}</div>}
        {message && <div className="inventory-scan-success">{message}</div>}

        <section className="inventory-card">
          <div className="inventory-section-head">
            <div>
              <h2>1. Choose the physical container</h2>
              <p>The closeout list is generated from the exact physical units currently saved in that container.</p>
            </div>
          </div>
          <div className="season-closeout-controls">
            <label>Container
              <select value={targetId} onChange={event => chooseTarget(event.target.value)}>
                {targets.map(item => <option key={item.id} value={item.id}>{item.name} — {item.id}{item.seasonalStatus === 'oos' ? ' — OOS' : ''}</option>)}
              </select>
            </label>
            <label>Closeout year
              <input type="number" min="2026" max="2100" value={closeoutYear} onChange={event => { setCloseoutYear(Number(event.target.value) || new Date().getFullYear()); setStarted(false); clearPending(); setReturnManifest([]); }} />
            </label>
          </div>
          <div className="season-closeout-rule">
            <strong>{closeoutYear} closeout rule</strong>
            <span>Return every catalog item classified Medication, plus non-medication stock expiring in {Number(closeoutYear) + 1}. Anything already expired or expiring during {closeoutYear} is also pulled.</span>
          </div>
          <button type="button" className="season-closeout-start" onClick={() => { setStarted(true); setMessage(`Season closeout started for ${target.name}. Scan medications to build the return manifest; scan dated supplies out normally.`); }} disabled={status !== 'ready'}>
            {started ? `Closeout active — ${target.id}` : `Begin season closeout — ${target.id}`}
          </button>
        </section>

        {status === 'loading' && <section className="inventory-card">Loading physical inventory…</section>}

        {status === 'ready' && (
          <>
            <div className="inventory-summary season-closeout-summary">
              <button type="button"><small>Required to pull</small><strong>{plan.requiredUnits}</strong></button>
              <button type="button"><small>Medication units</small><strong>{plan.medicationUnits}</strong></button>
              <button type="button"><small>Staged meds</small><strong>{manifestSummary.units}</strong></button>
              <button type="button"><small>Stay assigned</small><strong>{plan.remainingAssignedUnits}</strong></button>
            </div>

            <section className={`inventory-card season-closeout-status${plan.complete ? ' complete' : ''}`}>
              <div>
                <h2>{target.name}</h2>
                <p>{plan.complete ? 'No required closeout inventory remains.' : `${plan.requiredUnits} physical unit${plan.requiredUnits === 1 ? '' : 's'} still need to come out.`}</p>
              </div>
              <strong>{plan.complete ? 'CLOSEOUT COMPLETE' : `${plan.requiredUnits} REMAINING`}</strong>
            </section>

            {started && !plan.complete && (
              <section className="inventory-card">
                <div className="inventory-section-head">
                  <div><h2>2. Scan items</h2><p>Medication scans pull up the lots/dates already saved. Dated supplies continue to scan out individually.</p></div>
                </div>
                <InventoryBarcodeScanner onScan={handleScan} />

                {pendingMedicationRows.length > 0 && (
                  <div className="season-closeout-match" style={{ marginTop: 16, border: '2px solid rgba(217,164,65,.55)' }}>
                    <strong>Medication recognized · scan {pendingMedicationLabel}</strong>
                    <div style={{ marginTop: 8, lineHeight: 1.5 }}>
                      <strong style={{ fontSize: 19 }}>{pendingMedicationRows[0].item}</strong>
                      <div style={{ opacity: .78 }}>These are the lot/expiration records already saved in {target.name}. Physically verify them — no retyping required.</div>
                    </div>
                    {pendingMedicationRows.map(row => (
                      <div key={row.record.recordId} style={{ display: 'flex', justifyContent: 'space-between', gap: 14, padding: '10px 0', borderTop: '1px solid rgba(255,255,255,.1)' }}>
                        <span><strong>{row.lotSerial ? `Lot ${row.lotSerial}` : 'No lot recorded'}</strong><br /><small>{row.catalogId} · Qty {row.quantity}</small></span>
                        <span style={{ textAlign: 'right' }}><strong>{row.expirationDate ? formatDate(row.expirationDate) : 'Expiration N/A'}</strong><br /><small>{row.reason.label}</small></span>
                      </div>
                    ))}
                    <button type="button" onClick={stageMedicationRows} disabled={Boolean(savingId)} style={{ width: '100%', marginTop: 12, fontWeight: 900 }}>
                      I HAVE THESE · ADD {pendingMedicationSummary.units} UNIT{pendingMedicationSummary.units === 1 ? '' : 'S'} TO RETURN LIST
                    </button>
                  </div>
                )}

                {pendingCandidates.length > 0 && (
                  <div className="season-closeout-match">
                    <strong>Choose the unit matching scan {pendingScanLabel}</strong>
                    {pendingCandidates.map(row => (
                      <button type="button" key={row.record.recordId} onClick={() => returnRow(row)} disabled={savingId === row.record.recordId}>
                        <span><strong>{row.item}</strong><small>{row.component} · {row.reason.label}</small></span>
                        <span><strong>{row.lotSerial ? `Lot ${row.lotSerial}` : 'No lot'}</strong><small>{formatDate(row.expirationDate)}</small></span>
                      </button>
                    ))}
                  </div>
                )}
              </section>
            )}

            {returnManifest.length > 0 && (
              <section className="inventory-card" style={{ border: '2px solid rgba(217,164,65,.48)' }}>
                <div className="inventory-section-head">
                  <div>
                    <h2>3. Medication return manifest</h2>
                    <p>Nothing below has been removed from active inventory yet. Review the saved lot and expiration information, then confirm once.</p>
                  </div>
                  <strong>{manifestSummary.units} UNIT{manifestSummary.units === 1 ? '' : 'S'}</strong>
                </div>

                {returnManifest.map(row => (
                  <div className="season-closeout-row" key={`manifest-${row.record.recordId}`}>
                    <span className="season-closeout-item">
                      <strong>{row.item}</strong>
                      <small>{row.catalogId} · {row.component} · Qty {row.quantity}</small>
                    </span>
                    <span className="season-closeout-trace">
                      <strong>{row.lotSerial ? `Lot ${row.lotSerial}` : 'No lot recorded'}</strong>
                      <small>{row.expirationDate ? `Exp ${formatDate(row.expirationDate)}` : 'Expiration N/A / not dated'}</small>
                    </span>
                    <button type="button" onClick={() => removeManifestRow(row.record.recordId)} disabled={savingId === 'manifest'}>Remove</button>
                  </div>
                ))}

                <div style={{ marginTop: 16, padding: 14, borderRadius: 12, background: 'rgba(217,164,65,.08)', lineHeight: 1.5 }}>
                  <strong>Final confirmation</strong><br />
                  {manifestSummary.medicationCount} medication{manifestSummary.medicationCount === 1 ? '' : 's'} · {manifestSummary.recordCount} saved lot/date record{manifestSummary.recordCount === 1 ? '' : 's'} · {manifestSummary.units} physical unit{manifestSummary.units === 1 ? '' : 's'}.
                </div>
                <button type="button" className="season-closeout-start" onClick={confirmReturnManifest} disabled={Boolean(savingId)} style={{ marginTop: 12 }}>
                  {savingId === 'manifest' ? 'Confirming return…' : `Confirm Return & Archive · ${manifestSummary.units} Unit${manifestSummary.units === 1 ? '' : 's'}`}
                </button>
              </section>
            )}

            <section className="inventory-card season-closeout-list">
              <div className="inventory-section-head">
                <div>
                  <h2>{started ? (returnManifest.length ? '4' : '3') : '2'}. Required pull list</h2>
                  <p>Everything shown below must leave {target.name}. Medications can be staged by scanning the product once; dated supplies can still be returned individually.</p>
                </div>
                <button type="button" onClick={load}>Refresh</button>
              </div>

              {plan.complete && <div className="inventory-empty">Nothing remains on the closeout list for this container.</div>}
              {grouped.map(([component, rows]) => (
                <div className="season-closeout-group" key={component}>
                  <h3>{component}</h3>
                  {rows.map(row => {
                    const staged = manifestRecordIds.has(row.record.recordId);
                    return (
                      <div className="season-closeout-row" key={row.record.recordId} style={staged ? { opacity: .62 } : undefined}>
                        <span className="season-closeout-item">
                          <strong>{row.item}{staged ? ' · STAGED' : ''}</strong>
                          <small>{row.itemType || 'Unclassified'} · {row.catalogId} · {row.reason.label}</small>
                        </span>
                        <span className="season-closeout-trace">
                          <strong>{row.lotSerial ? `Lot ${row.lotSerial}` : 'No lot recorded'}</strong>
                          <small>{row.expirationDate ? `Exp ${formatDate(row.expirationDate)}` : 'Expiration N/A / not dated'}{row.quantity > 1 ? ` · Qty ${row.quantity}` : ''}</small>
                        </span>
                        <button type="button" onClick={() => row.reason.medication ? setPendingMedicationRows(plan.rows.filter(item => item.reason.medication && item.catalogId === row.catalogId && !manifestRecordIds.has(recordId(item)))) : returnRow(row)} disabled={!started || Boolean(savingId) || staged}>
                          {staged ? 'In return list' : row.reason.medication ? 'Confirm saved lots' : savingId === row.record.recordId ? 'Returning…' : row.quantity > 1 ? `Return qty ${row.quantity}` : 'Return manually'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              ))}
            </section>
          </>
        )}
      </main>
    </div>
  );
}
